/**
 * syncDemoStockMovementsFromVentas.ts — Backfill de auditoría de stock desde ventas demo.
 *
 * QUÉ HACE: Genera movimientos venta_caja en inventory_stock_movements y ajusta stock
 * para cada línea de las 180+ ventas de la jornada multicaja (y ticket regresión).
 * QUÉ NO DEBE HACER: Tocar ventas del usuario ni movimientos manuales de inventario.
 */

import { setStorage, getStorage } from "@/mocks/helpers/storage";
import { txRead } from "@/lib/persistence/transactionalStore";
import { defaultProducts } from "@/mocks/data/products";
import { emitStockChange } from "@/lib/stockSync";
import type { Producto, StockMovimiento } from "@/types";
import {
  applyStockDeltaToProduct,
  parseVariantKeyFromLineNombre,
  stockVendibleProducto,
} from "@/components/ui/variants/variantHelpers";
import type { Venta } from "../types/caja.types";
import { MAY12_DEMO_SEED_VERSION } from "./may12CajaDemoSeed.data";
import { isDemoVentaId } from "./syncDemoHistorialFromCierres";
import { isDemoShowcaseVentaId } from "./demoShowcaseHistorial.data";
import { resolveDemoCatalogLine } from "./demoInventoryProductMap";

import { defaultStockMovements } from "@/mocks/data/stockMovements";

const LS_USER_REAL_POS = "altenot_caja_user_real_pos";

const LS_STOCK_SYNC_VERSION = "altenot_caja_demo_stock_sync";

export const DEMO_STOCK_SYNC_VERSION = "stock-sync-v5";

export function isDemoStockMovimientoId(id: string): boolean {
  return id.startsWith("demo-sm-");
}

function readInventoryProducts(): Array<Record<string, unknown>> {
  return getStorage("inventory_products", defaultProducts) as unknown as Array<
    Record<string, unknown>
  >;
}

/** Catálogo base para sync demo: stock/SKUs desde defaults; resto desde storage. */
function buildDemoStockSyncBaseline(): Array<Record<string, unknown>> {
  const stored = readInventoryProducts();
  const storedById = new Map(stored.map((p) => [String(p.id), p]));
  const defaultIds = new Set(defaultProducts.map((p) => String(p.id)));

  const baseline = defaultProducts.map((def) => {
    const current = storedById.get(String(def.id));
    if (!current) {
      return JSON.parse(JSON.stringify(def)) as Record<string, unknown>;
    }
    return {
      ...current,
      stock: def.stock,
      variantesSKUs: def.variantesSKUs
        ? JSON.parse(JSON.stringify(def.variantesSKUs))
        : current.variantesSKUs,
    };
  });

  for (const p of stored) {
    if (!defaultIds.has(String(p.id))) baseline.push({ ...p });
  }

  return baseline;
}

function readStockMovements(): StockMovimiento[] {
  return getStorage(
    "inventory_stock_movements",
    defaultStockMovements,
  ) as StockMovimiento[];
}

function syncStorageKey(): string {
  return `${DEMO_STOCK_SYNC_VERSION}:${MAY12_DEMO_SEED_VERSION}`;
}

type SaleLine = {
  ventaId: string;
  fecha: string;
  userName: string;
  puntoNombre: string;
  productoId: string;
  nombre: string;
  cantidad: number;
  tipo: "venta_caja" | "devolucion" | "anulacion";
  nota: string;
};

function isDemoVentaForStockSync(ventaId: string): boolean {
  return isDemoVentaId(ventaId) || isDemoShowcaseVentaId(ventaId);
}

function pushCatalogLine(
  lines: SaleLine[],
  venta: Venta,
  productoId: string,
  nombre: string,
  cantidad: number,
  tipo: SaleLine["tipo"],
  fecha: string,
  nota: string,
): void {
  const resolved = resolveDemoCatalogLine(
    productoId,
    nombre,
    venta.puntoCajaId,
  );
  if (!resolved.productoId.startsWith("cat_")) return;
  lines.push({
    ventaId: venta.id,
    fecha,
    userName: venta.userName ?? "Cajero",
    puntoNombre: venta.puntoCajaNombre ?? "",
    productoId: resolved.productoId,
    nombre: resolved.nombre,
    cantidad,
    tipo,
    nota,
  });
}

function flattenDemoSaleLines(ventas: Venta[]): SaleLine[] {
  const lines: SaleLine[] = [];
  for (const venta of ventas) {
    if (!isDemoVentaForStockSync(venta.id)) continue;

    if (venta.estado !== "anulada") {
      for (const line of venta.productos ?? []) {
        pushCatalogLine(
          lines,
          venta,
          line.productoId,
          line.nombre,
          line.cantidad ?? 1,
          "venta_caja",
          venta.fecha,
          venta.puntoCajaNombre
            ? `Venta demo — ${venta.puntoCajaNombre}`
            : "Venta demo en caja",
        );
      }
    }

    for (const dev of venta.devoluciones ?? []) {
      for (const line of dev.items) {
        pushCatalogLine(
          lines,
          venta,
          line.productoId,
          line.nombre,
          line.cantidad ?? 1,
          "devolucion",
          dev.fecha,
          `Devolución demo — ${dev.motivoDevolucion ?? "producto"}`,
        );
      }
    }

    for (const anu of venta.anulaciones ?? []) {
      for (const line of anu.items) {
        pushCatalogLine(
          lines,
          venta,
          line.productoId,
          line.nombre,
          line.cantidad ?? 1,
          "anulacion",
          anu.fecha,
          `Anulación parcial demo — ${anu.motivoAnulacion ?? "ítem"}`,
        );
      }
    }

    if (venta.estado === "anulada") {
      for (const line of venta.productos ?? []) {
        pushCatalogLine(
          lines,
          venta,
          line.productoId,
          line.nombre,
          line.cantidad ?? 1,
          "anulacion",
          venta.anuladaEn ?? venta.fecha,
          "Anulación total demo",
        );
      }
    }
  }
  return lines.sort(
    (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
  );
}

function shouldSkipDemoStockSync(): boolean {
  try {
    if (localStorage.getItem(LS_USER_REAL_POS) === "1") return true;
  } catch {
    /* ignore */
  }
  return false; // Permitimos el backfill de movimientos demo incluso si hay ventas del usuario, para que el historial sea coherente
}

/**
 * Escribe movimientos demo y descuenta stock en inventory_products.
 * @returns true si hubo escritura
 */
export function syncDemoStockMovementsFromVentas(
  ventas: Venta[],
  options?: { force?: boolean },
): boolean {
  if (shouldSkipDemoStockSync()) return false;

  const key = syncStorageKey();
  try {
    if (
      !options?.force &&
      localStorage.getItem(LS_STOCK_SYNC_VERSION) === key
    ) {
      return false;
    }
  } catch {
    /* ignore */
  }

  const saleLines = flattenDemoSaleLines(ventas);
  if (saleLines.length === 0) return false;

  const products = buildDemoStockSyncBaseline();
  const productById = new Map(products.map((p) => [String(p.id), p] as const));

  const demoMovs: StockMovimiento[] = [];

  // 1. Cargar movimientos iniciales (creación) desde los defaults del sistema
  const defaultMovs = defaultStockMovements.filter((m) =>
    m.id.startsWith("mov_demo_"),
  );
  demoMovs.push(...defaultMovs);

  // 2. Generar movimientos desde las ventas calculadas
  for (const line of saleLines) {
    const product = productById.get(line.productoId);
    if (!product) continue;

    const esEntrada = line.tipo === "devolucion" || line.tipo === "anulacion";
    const delta = esEntrada ? line.cantidad : -line.cantidad;
    const catalogProduct = product as unknown as Producto;
    const variantKey = parseVariantKeyFromLineNombre(
      catalogProduct,
      line.nombre,
    );

    const stockAntes = stockVendibleProducto(catalogProduct);
    const updated = applyStockDeltaToProduct(
      product as unknown as Producto,
      delta,
      variantKey,
    );
    productById.set(line.productoId, updated);
    const stockDespues = stockVendibleProducto(updated as unknown as Producto);

    demoMovs.push({
      id: `demo-sm-${line.tipo}-${line.ventaId}-${line.productoId}-${line.fecha}`,
      productoId: line.productoId,
      tipo: line.tipo,
      cantidad: line.cantidad,
      stockAntes,
      stockDespues,
      nota: line.nota,
      usuarioNombre: line.userName,
      ventaId: line.ventaId,
      createdAt: line.fecha,
    });
  }

  // 3. Mantener movimientos manuales del usuario (si existen)
  const userMovs = readStockMovements().filter(
    (m) => !isDemoStockMovimientoId(m.id) && !m.id.startsWith("mov_demo_"),
  );

  setStorage(
    "inventory_products",
    products.map((p) => productById.get(String(p.id)) ?? p),
  );
  // Los userMovs van primero (más recientes de la sesión actual) seguidos de demoMovs
  setStorage("inventory_stock_movements", [...userMovs, ...demoMovs.reverse()]);

  try {
    localStorage.setItem(LS_STOCK_SYNC_VERSION, key);
    emitStockChange();
    window.dispatchEvent(new CustomEvent("caja-demo-stock-synced"));
  } catch {
    /* ignore */
  }

  return true;
}

/** Sincroniza stock desde ventas actuales en caja_ventas_historial. */
export function ensureDemoStockSyncedFromHistorial(options?: {
  force?: boolean;
}): boolean {
  if (shouldSkipDemoStockSync()) return false;
  try {
    const parsed = txRead<Venta[]>("caja_ventas_historial", []);
    if (!Array.isArray(parsed)) return false;
    return syncDemoStockMovementsFromVentas(parsed as Venta[], options);
  } catch {
    return false;
  }
}
